/* eslint-disable no-unsafe-optional-chaining */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { task, dropTask, enqueueTask, timeout } from 'ember-concurrency';
import type AjaxService from 'garaje/services/ajax';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { RoleId, RoleScopeType } from 'garaje/utils/custom-roles';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';

const SAVE_NOTIFICATION_SETTING_DEBOUNCE = zft(1000);

type ResponseDataAttributes = {
  acsFailureNotificationsEnabled: boolean;
  dataMissingNotificationsEnabled: boolean;
  resolutionNotificationsEnabled: boolean;
  lastModifiedByUserId: number;
  timezone: string;
  createdAt: string;
  updatedAt: string;
};

type ResponseData = {
  attributes: ResponseDataAttributes;
  id: string;
  type: string;
};

type AcsLocationSettingsResponse = {
  data: ResponseData[];
};

type RecipientAttributes = {
  recipientType: string;
  recipientValue: string;
  locationId: number;
  alertType: string;
  createdAt: string;
  updatedAt: string;
  lastModifiedByUserId: number;
};

type RecipientData = {
  id: string;
  type: string;
  attributes: RecipientAttributes;
};

type RecipientsApiResponse = {
  data?: RecipientData[];
};

export default class AppsSettingsController extends Controller {
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;
  @service declare ajax: AjaxService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare store: Store;

  @tracked acsFailureNotificationsEnabled = false;
  @tracked dataMissingNotificationsEnabled = false;
  @tracked resolutionNotificationsEnabled = false;
  @tracked users: Array<{ id: string; name: string }> = [];
  @tracked roles: Array<{ name: string }> = [];

  // ACS Failure alert recipients
  @tracked acsFailureSelectedUsers: Array<{ id: string; name: string }> = [];
  @tracked acsFailureSelectedRoles: Array<{ name: string }> = [];

  // Data Missing alert recipients
  @tracked dataMissingSelectedUsers: Array<{ id: string; name: string }> = [];
  @tracked dataMissingSelectedRoles: Array<{ name: string }> = [];

  // Resolution alert recipients
  @tracked resolutionSelectedUsers: Array<{ id: string; name: string }> = [];
  @tracked resolutionSelectedRoles: Array<{ name: string }> = [];

  get showAccessControlAlertSettings(): boolean {
    return (
      this.featureFlags.isEnabled('show-acs-failure-alert-toggle') ||
      this.featureFlags.isEnabled('show-data-missing-alert-toggle')
    );
  }

  get showSecureOnPremiseSettings(): boolean {
    return (
      this.featureFlags.isEnabled('show-binary-download-modal') ||
      this.featureFlags.isEnabled('show-diplomat-client-config-modal')
    );
  }

  get isResolutionToggleDisabled(): boolean {
    // Resolution toggle should be disabled if both ACS failure and Data Missing are disabled
    return !this.acsFailureNotificationsEnabled && !this.dataMissingNotificationsEnabled;
  }

  @action
  refreshDiplomatClients(): void {
    this.send('refreshModel');
  }

  toggleNotificationSetting = task(async (type: string, value: boolean) => {
    switch (type) {
      case 'acsFailure':
        this.acsFailureNotificationsEnabled = value;
        break;
      case 'dataMissing':
        this.dataMissingNotificationsEnabled = value;
        break;
      case 'resolution':
        this.resolutionNotificationsEnabled = value;
        break;
    }

    // Automatically disable resolution notifications if both failure notifications are disabled
    if (this.isResolutionToggleDisabled && this.resolutionNotificationsEnabled) {
      this.resolutionNotificationsEnabled = false;
    }

    await this.saveNotificationSettings.perform();
  });

  loadNotificationSettings = dropTask(async () => {
    const locationId = this.state?.currentLocation?.id;
    const url = urlBuilder.locationNotificationSettingsUrl(locationId);
    try {
      const response: AcsLocationSettingsResponse = await this.ajax.request<AcsLocationSettingsResponse>(url, {
        // @ts-ignore accept is not a valid attribute for request options
        accept: 'application/vnd.api+json',
      });
      const { data } = response;
      if (data.length !== 0) {
        this.acsFailureNotificationsEnabled = data[0]?.attributes.acsFailureNotificationsEnabled || false;
        this.dataMissingNotificationsEnabled = data[0]?.attributes.dataMissingNotificationsEnabled || false;
        this.resolutionNotificationsEnabled = data[0]?.attributes.resolutionNotificationsEnabled || false;
        return data[0]?.attributes;
      }
      return;
    } catch (_e) {
      return;
    }
  });

  saveNotificationSettings = dropTask(async () => {
    const locationId = this.state?.currentLocation?.id;
    const timezone = this.state?.currentLocation?.timezone;
    const lastModifiedByUserId = this.state?.currentUser?.id;
    const { acsFailureNotificationsEnabled, dataMissingNotificationsEnabled, resolutionNotificationsEnabled } = this;
    const url = urlBuilder.locationNotificationSettingsUrl(locationId);

    await timeout(SAVE_NOTIFICATION_SETTING_DEBOUNCE);

    try {
      await this.ajax.request(url, {
        type: 'PATCH',
        contentType: 'application/json',
        // @ts-ignore accept is not a valid attribute for request options
        accept: 'application/vnd.api+json',
        data: {
          timezone,
          acsFailureNotificationsEnabled,
          dataMissingNotificationsEnabled,
          resolutionNotificationsEnabled,
          lastModifiedByUserId,
        },
      });
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error saving changes.');
    }
  });

  loadUsersAndRoles = dropTask(async () => {
    const locationId = this.state?.currentLocation?.id;
    const companyId = this.state?.currentCompany?.id;
    if (!locationId || !companyId) {
      return;
    }

    try {
      // Query both location-specific roles and company-level roles in parallel
      const [locationRoles, companyRoles] = await Promise.all([
        // Query location-roles for this specific location
        this.store.query('location-role', {
          filter: { location: locationId },
          include: 'user',
        }),
        // Query company-level role assignments (Global Admins, Billing Admins, etc.)
        this.store.query('location-role', {
          filter: { company: companyId },
          include: 'user',
        }),
      ]);

      // Extract unique users from both queries
      const usersMap = new Map<string, { id: string; name: string }>();

      // Helper function to process role assignments
      const processRoleAssignments = async (roleAssignments: unknown[]) => {
        for (const roleAssignment of roleAssignments) {
          try {
            // User relationship is async, need to await it
            // @ts-ignore - roleAssignment is from ember-data query
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const user = await roleAssignment.user;

            // Add user if not already in map
            if (user) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              const userId = user.id;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              const fullName = user.fullName;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              const email = user.email;

              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              if (userId && !usersMap.has(userId)) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const userName = fullName || email || 'Unknown User';
                // eslint-disable-next-line ember/use-ember-get-and-set, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument
                usersMap.set(userId, {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  id: userId,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  name: userName,
                });
              }
            }
          } catch (error) {
            // Continue processing other users if one fails
            // eslint-disable-next-line no-console
            console.warn('Error processing role assignment:', error);
          }
        }
      };

      // Process location-specific role assignments
      await processRoleAssignments(locationRoles.toArray());

      // Process company-level role assignments
      await processRoleAssignments(companyRoles.toArray());

      this.users = Array.from(usersMap.values());

      // Fetch roles from authz API and filter using the same logic as /manage/roles
      await this.authz.fetchRoles();
      this.roles = this.authz.roles
        .filter((role) => {
          // Filter out deactivated roles
          if (role.deactivated) {
            return false;
          }

          // Filter based on role scope
          if (role.roleScope === RoleScopeType.COMPANY) {
            // Filter out the company start-from-scratch role
            if (role.id === RoleId.COMPANY_START_FROM_SCRATCH) {
              return false;
            }
            // Only show Analytics Viewer for premium accounts
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            if (role.id === RoleId.ANALYTICS_VIEWER && role.type === 'default') {
              return !!this.state.features?.canAccessAnalyticsViewerRole;
            }
            return true;
          } else if (role.roleScope === RoleScopeType.LOCATION) {
            // Filter out Employee and location start-from-scratch roles
            if (role.id === RoleId.EMPLOYEE || role.id === RoleId.LOCATION_START_FROM_SCRATCH) {
              return false;
            }
            return true;
          } else if (role.roleScope === RoleScopeType.ZONE) {
            // Show all zone roles
            return true;
          }

          return false;
        })
        .map((role) => ({ name: role.name }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading users and roles:', error);
      this.flashMessages.showAndHideFlash('error', 'Error loading users and roles.');
    }
  });

  loadNotificationRecipients = dropTask(async () => {
    const locationId = this.state?.currentLocation?.id;
    if (!locationId) {
      return;
    }

    try {
      // Load all recipients in parallel
      const acsFailureUrl = urlBuilder.locationNotificationRecipientsUrl(locationId, 'acs_failure');
      const dataMissingUrl = urlBuilder.locationNotificationRecipientsUrl(locationId, 'data_missing');

      const requests: Promise<RecipientsApiResponse>[] = [
        this.ajax.request(acsFailureUrl, {
          // @ts-ignore accept is not a valid attribute for request options
          accept: 'application/vnd.api+json',
        }),
        this.ajax.request(dataMissingUrl, {
          // @ts-ignore accept is not a valid attribute for request options
          accept: 'application/vnd.api+json',
        }),
      ];

      // Only fetch resolution recipients if feature flag is enabled
      if (this.featureFlags.isEnabled('badge-log-data-alerts-m3')) {
        const resolutionUrl = urlBuilder.locationNotificationRecipientsUrl(locationId, 'resolution');
        requests.push(
          this.ajax.request(resolutionUrl, {
            // @ts-ignore accept is not a valid attribute for request options
            accept: 'application/vnd.api+json',
          }),
        );
      }

      const responses = await Promise.all(requests);
      const [acsFailureResponse, dataMissingResponse, resolutionResponse] = responses;

      // Parse ACS Failure recipients
      if (acsFailureResponse?.data && acsFailureResponse.data.length > 0) {
        this.acsFailureSelectedUsers = acsFailureResponse.data
          .filter((r: RecipientData) => r.attributes.recipientType === 'user')
          .map((r: RecipientData) => {
            const userId = r.attributes.recipientValue;
            return this.users.find((u) => u.id === userId) || { id: userId, name: `User ${userId}` };
          });

        this.acsFailureSelectedRoles = acsFailureResponse.data
          .filter((r: RecipientData) => r.attributes.recipientType === 'role')
          .map((r: RecipientData) => ({ name: r.attributes.recipientValue }));
      } else {
        // EIP-512: Set default roles when no custom recipients configured
        this.acsFailureSelectedRoles = [{ name: 'Global Admin' }, { name: 'Location Admin' }];
      }

      // Parse Data Missing recipients
      if (dataMissingResponse?.data && dataMissingResponse.data.length > 0) {
        this.dataMissingSelectedUsers = dataMissingResponse.data
          .filter((r: RecipientData) => r.attributes.recipientType === 'user')
          .map((r: RecipientData) => {
            const userId = r.attributes.recipientValue;
            return this.users.find((u) => u.id === userId) || { id: userId, name: `User ${userId}` };
          });

        this.dataMissingSelectedRoles = dataMissingResponse.data
          .filter((r: RecipientData) => r.attributes.recipientType === 'role')
          .map((r: RecipientData) => ({ name: r.attributes.recipientValue }));
      } else {
        // EIP-512: Set default roles when no custom recipients configured
        this.dataMissingSelectedRoles = [{ name: 'Global Admin' }, { name: 'Location Admin' }];
      }

      // Parse Resolution recipients
      if (resolutionResponse?.data && resolutionResponse.data.length > 0) {
        this.resolutionSelectedUsers = resolutionResponse.data
          .filter((r: RecipientData) => r.attributes.recipientType === 'user')
          .map((r: RecipientData) => {
            const userId = r.attributes.recipientValue;
            return this.users.find((u) => u.id === userId) || { id: userId, name: `User ${userId}` };
          });

        this.resolutionSelectedRoles = resolutionResponse.data
          .filter((r: RecipientData) => r.attributes.recipientType === 'role')
          .map((r: RecipientData) => ({ name: r.attributes.recipientValue }));
      } else {
        // EIP-512: Set default roles when no custom recipients configured
        this.resolutionSelectedRoles = [{ name: 'Global Admin' }, { name: 'Location Admin' }];
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading notification recipients:', error);
      this.flashMessages.showAndHideFlash('error', 'Error loading notification recipients.');
    }
  });

  @action
  onAcsFailureUsersChange(selectedUsers: Array<{ id: string; name: string }>): void {
    // Deduplicate by id
    const uniqueUsers = Array.from(new Map(selectedUsers.map((user) => [user.id, user])).values());
    this.acsFailureSelectedUsers = uniqueUsers;
    void this.saveNotificationRecipients.perform('acs_failure');
  }

  @action
  onAcsFailureRolesChange(selectedRoles: Array<{ name: string }>): void {
    // Deduplicate by name
    const uniqueRoles = Array.from(new Map(selectedRoles.map((role) => [role.name, role])).values());
    this.acsFailureSelectedRoles = uniqueRoles;
    void this.saveNotificationRecipients.perform('acs_failure');
  }

  @action
  onDataMissingUsersChange(selectedUsers: Array<{ id: string; name: string }>): void {
    // Deduplicate by id
    const uniqueUsers = Array.from(new Map(selectedUsers.map((user) => [user.id, user])).values());
    this.dataMissingSelectedUsers = uniqueUsers;
    void this.saveNotificationRecipients.perform('data_missing');
  }

  @action
  onDataMissingRolesChange(selectedRoles: Array<{ name: string }>): void {
    // Deduplicate by name
    const uniqueRoles = Array.from(new Map(selectedRoles.map((role) => [role.name, role])).values());
    this.dataMissingSelectedRoles = uniqueRoles;
    void this.saveNotificationRecipients.perform('data_missing');
  }

  @action
  onResolutionUsersChange(selectedUsers: Array<{ id: string; name: string }>): void {
    // Deduplicate by id
    const uniqueUsers = Array.from(new Map(selectedUsers.map((user) => [user.id, user])).values());
    this.resolutionSelectedUsers = uniqueUsers;
    void this.saveNotificationRecipients.perform('resolution');
  }

  @action
  onResolutionRolesChange(selectedRoles: Array<{ name: string }>): void {
    // Deduplicate by name
    const uniqueRoles = Array.from(new Map(selectedRoles.map((role) => [role.name, role])).values());
    this.resolutionSelectedRoles = uniqueRoles;
    void this.saveNotificationRecipients.perform('resolution');
  }

  saveNotificationRecipients = enqueueTask(async (alertType: string) => {
    const locationId = this.state?.currentLocation?.id;
    const lastModifiedByUserId = this.state?.currentUser?.id;

    // Select the correct users and roles based on alert type
    let selectedUsers: Array<{ id: string; name: string }>;
    let selectedRoles: Array<{ name: string }>;

    if (alertType === 'acs_failure') {
      selectedUsers = this.acsFailureSelectedUsers;
      selectedRoles = this.acsFailureSelectedRoles;
    } else if (alertType === 'data_missing') {
      selectedUsers = this.dataMissingSelectedUsers;
      selectedRoles = this.dataMissingSelectedRoles;
    } else {
      selectedUsers = this.resolutionSelectedUsers;
      selectedRoles = this.resolutionSelectedRoles;
    }

    // Validate that selected users still exist
    const validUserIds = new Set(this.users.map((u) => u.id));
    const validUsers = selectedUsers.filter((user) => validUserIds.has(user.id));

    // Validate that selected roles still exist
    const validRoleNames = new Set(this.roles.map((r) => r.name));
    const validRoles = selectedRoles.filter((role) => validRoleNames.has(role.name));

    // Build recipients array with validated users and roles
    const recipients = [
      ...validUsers.map((user) => ({
        recipientType: 'user',
        recipientValue: user.id,
      })),
      ...validRoles.map((role) => ({
        recipientType: 'role',
        recipientValue: role.name,
      })),
    ];

    const url = urlBuilder.locationNotificationRecipientsUrl(locationId, alertType);

    try {
      await this.ajax.request(url, {
        type: 'PUT',
        contentType: 'application/json',
        // @ts-ignore accept is not a valid attribute for request options
        accept: 'application/vnd.api+json',
        data: {
          alertType,
          recipients,
          lastModifiedByUserId,
        },
      });
      this.flashMessages.showAndHideFlash('success', 'Notification recipients saved!');
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error saving notification recipients.');
    }
  });
}
